import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Lock,
  LogOut,
  Mail,
  Moon,
  Phone,
  RefreshCw,
  Sun,
  Video,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadsDesk({ theme, onToggleTheme }) {
  const [deskKey, setDeskKey] = useState(
    () => localStorage.getItem("dn-desk-key") || ""
  );
  const [passcode, setPasscode] = useState("");
  const [status, setStatus] = useState(deskKey ? "loading" : "locked");
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async (key) => {
    setStatus("loading");
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: { "x-desk-passcode": key },
      });
      setLeads(data);
      setStatus("ready");
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("dn-desk-key");
        setDeskKey("");
        setStatus("locked");
      } else {
        setStatus("error");
      }
    }
  }, []);

  useEffect(() => {
    if (deskKey) load(deskKey);
  }, [deskKey, load]);

  const unlock = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await axios.post(`${API}/desk/unlock`, { passcode });
      localStorage.setItem("dn-desk-key", passcode);
      setPasscode("");
      setDeskKey(passcode);
    } catch {
      setError("Invalid passcode. Ask the desk head for access.");
    }
  };

  const lock = () => {
    localStorage.removeItem("dn-desk-key");
    setDeskKey("");
    setLeads([]);
    setStatus("locked");
  };

  if (status === "locked") {
    return (
      <div
        className="blueprint-bg flex min-h-screen items-center justify-center bg-background px-5"
        data-testid="desk-locked"
      >
        <div className="w-full max-w-sm border border-border bg-card p-8">
          <span className="relative flex h-10 w-10 items-center justify-center border-2 border-foreground">
            <Lock className="h-4 w-4 text-accent" />
          </span>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
            Team Desk — Restricted
          </p>
          <h1 className="mt-2 font-display text-3xl font-medium uppercase tracking-tight">
            Sales desk.
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Enter the shared desk passcode to view recorded enquiries.
          </p>
          <form onSubmit={unlock} className="mt-6 space-y-3">
            <Input
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Desk passcode"
              className="h-11 rounded-none border-input font-mono text-sm"
              data-testid="desk-passcode-input"
            />
            {error && (
              <p
                className="border border-destructive/40 bg-destructive/10 px-3 py-2 font-mono text-xs text-destructive"
                data-testid="desk-unlock-error"
              >
                {error}
              </p>
            )}
            <Button
              type="submit"
              className="h-11 w-full rounded-none font-mono text-xs uppercase tracking-[0.2em]"
              data-testid="desk-unlock-btn"
            >
              Unlock Desk
            </Button>
          </form>
          <Link
            to="/"
            className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground"
            data-testid="desk-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to marketplace
          </Link>
        </div>
      </div>
    );
  }

  const visits = leads.filter((l) => l.mode === "visit").length;
  const stats = [
    { label: "Total Enquiries", value: leads.length },
    { label: "Site Visits", value: visits },
    { label: "Express Interests", value: leads.length - visits },
    {
      label: "NRI Leads",
      value: leads.filter((l) => l.investor_profile === "NRI").length,
    },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="desk-page">
      <header className="border-b border-border bg-background/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-10">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground transition-colors duration-200 hover:text-foreground"
              data-testid="desk-back-link"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Marketplace
            </Link>
            <span className="hidden h-4 w-px bg-border sm:block" />
            <p className="font-mono text-xs uppercase tracking-[0.25em]">
              Team Desk
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(deskKey)}
              className="flex h-9 w-9 items-center justify-center border border-border transition-colors duration-200 hover:bg-secondary"
              data-testid="desk-refresh-btn"
              aria-label="Refresh leads"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={onToggleTheme}
              className="flex h-9 w-9 items-center justify-center border border-border transition-colors duration-200 hover:bg-secondary"
              data-testid="theme-toggle-btn"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={lock}
              className="flex h-9 items-center gap-2 border border-border px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-foreground"
              data-testid="desk-lock-btn"
            >
              <LogOut className="h-3.5 w-3.5" /> Lock
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 md:px-10 md:py-14">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">
          Enquiries — Live from the database
        </p>
        <h1 className="mt-3 font-display text-3xl font-medium uppercase tracking-tight md:text-4xl">
          Recorded interest
        </h1>

        <div
          className="mt-8 grid grid-cols-2 gap-px border border-border bg-border md:grid-cols-4"
          data-testid="desk-stats"
        >
          {stats.map((s) => (
            <div key={s.label} className="bg-background p-5">
              <p className="font-display text-3xl font-extrabold tracking-tight">
                {s.value}
              </p>
              <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                {s.label}
              </p>
            </div>
          ))}
        </div>

        {status === "loading" && (
          <div className="mt-8 space-y-2" data-testid="desk-loading">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-20 animate-pulse border border-border bg-secondary/50"
              />
            ))}
          </div>
        )}

        {status === "error" && (
          <div
            className="mt-8 border border-destructive/40 bg-destructive/10 p-6 font-mono text-xs text-destructive"
            data-testid="desk-error"
          >
            Could not reach the desk API. Hit refresh to retry.
          </div>
        )}

        {status === "ready" && leads.length === 0 && (
          <div
            className="mt-8 border border-dashed border-border p-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground"
            data-testid="desk-empty"
          >
            No enquiries yet — share the marketplace link
          </div>
        )}

        {status === "ready" && leads.length > 0 && (
          <div className="mt-8 border-t border-border" data-testid="desk-table">
            <div className="hidden grid-cols-12 gap-4 border-b border-border py-3 font-mono text-[9px] uppercase tracking-[0.25em] text-muted-foreground md:grid">
              <span className="col-span-3">Investor</span>
              <span className="col-span-2">Mode</span>
              <span className="col-span-2">Parcel</span>
              <span className="col-span-2">Phone</span>
              <span className="col-span-2">Manager</span>
              <span className="col-span-1 text-right">Date</span>
            </div>
            {leads.map((l) => (
              <div
                key={l.id}
                className="grid gap-3 border-b border-border py-4 md:grid-cols-12 md:items-center md:gap-4"
                data-testid={`lead-row-${l.id}`}
              >
                <div className="md:col-span-3">
                  <p className="font-medium">{l.name}</p>
                  <p className="mt-0.5 flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
                    <Mail className="h-3 w-3" /> {l.email}
                  </p>
                  <span className="mt-2 inline-block border border-border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">
                    {l.investor_profile}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <span
                    className={`inline-flex items-center gap-1.5 border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${
                      l.mode === "visit"
                        ? "border-ember/40 bg-ember/10 text-ember"
                        : "border-accent/40 bg-accent/10 text-accent"
                    }`}
                  >
                    {l.mode === "visit" && <Video className="h-3 w-3" />}
                    {l.mode === "visit" ? "Site Visit" : "Interest"}
                  </span>
                </div>
                <p className="font-mono text-xs md:col-span-2">
                  {l.property_name}
                </p>
                <p className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground md:col-span-2">
                  <Phone className="h-3 w-3" /> {l.country_code} {l.phone}
                </p>
                <p className="font-mono text-xs md:col-span-2">
                  {l.relationship_manager?.name}
                </p>
                <p className="font-mono text-[11px] text-muted-foreground md:col-span-1 md:text-right">
                  {new Date(l.created_at).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
